import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Col, Form, Row, Space, Tabs, message } from "antd";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";

import { ProfileSidebar } from "./partials/ProfileSidebar";
import { ProfileDetailsTab } from "./partials/ProfileDetailsTab";
import { ChangePasswordTab } from "./partials/ChangePasswordTab";
import { getPasswordStrength, getSessionProfileValues } from "./profile.utils";
import {
  useChangePasswordMutation,
  useSaveProfileMutation,
  useSecurityQuestionsQuery,
  useUserSecurityQuestionQuery,
} from "./profile.hooks";
import { PROFILE_TABS } from "./profile.constants";


export function Profile() {
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState(false);
  const [pwdStrength, setPwdStrength] = useState(0);
  const [activeTab, setActiveTab] = useState(PROFILE_TABS.PROFILE);
  const [profileForm] = Form.useForm();
  const [pwdForm] = Form.useForm();

  const sessionProfile = useMemo(() => getSessionProfileValues(), []);

  const securityQuestionsQuery = useSecurityQuestionsQuery();
  const userSecurityQuestionQuery = useUserSecurityQuestionQuery(
    sessionProfile.userId,
    !sessionProfile.questionId,
  );

  const saveProfileMutation = useSaveProfileMutation();
  const changePasswordMutation = useChangePasswordMutation();

  // Prefill both forms once the saved security question id (from session or API) is known.
  useEffect(() => {
    const resolvedQuestionId = sessionProfile.questionId || userSecurityQuestionQuery.data;

    profileForm.setFieldsValue({
      firstName: sessionProfile.firstName,
      lastName: sessionProfile.lastName,
      email: sessionProfile.email,
      department: sessionProfile.role,
      employeeId: sessionProfile.userId,
      questionId: resolvedQuestionId || undefined,
      securityAnswer: sessionProfile.securityAnswer,
    });

    pwdForm.setFieldsValue({
      questionId: resolvedQuestionId || undefined,
      securityAnswer: sessionProfile.securityAnswer,
    });
  }, [profileForm, pwdForm, sessionProfile, userSecurityQuestionQuery.data]);

  const handleEdit = useCallback(() => setEditMode(true), []);

  const handleCancelEdit = useCallback(() => {
    setEditMode(false);
    profileForm.resetFields();
  }, [profileForm]);

  const handleSaveProfile = useCallback(async () => {
    try {
      const values = await profileForm.validateFields();
      await saveProfileMutation.mutateAsync(values);

      pwdForm.setFieldsValue({
        questionId: values.questionId || undefined,
        securityAnswer: values.securityAnswer || "",
      });

      setEditMode(false);
      message.success("Profile updated successfully!");
    } catch {
      /* validation errors are shown inline by the form */
    }
  }, [profileForm, pwdForm, saveProfileMutation]);

  const handlePasswordFieldChange = useCallback((password) => {
    setPwdStrength(getPasswordStrength(password));
  }, []);

  const handleChangePassword = useCallback(async () => {
    try {
      const values = await pwdForm.validateFields();
      await changePasswordMutation.mutateAsync({
        userId: sessionProfile.userId,
        currentPassword: values.currentPassword,
        oldPassword: values.currentPassword,
        newPassword: values.newPassword,
        questionId: Number(values.questionId),
        securityAnswer: values.securityAnswer,
      });

      pwdForm.resetFields();
      setPwdStrength(0);
      message.success("Password changed successfully.");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to change password.";
      message.error(errorMessage);
    }
  }, [pwdForm, changePasswordMutation, sessionProfile.userId]);

 const handleSignOut = useCallback(() => {
  sessionStorage.clear();
  navigate("/watchtower");
}, [navigate]);

  const securityQuestions = securityQuestionsQuery.data ?? [];

  return (
    <div>
      <Row gutter={[20, 20]}>
        {/* Left panel — avatar + quick info */}
        <Col xs={24} lg={7}>
          <ProfileSidebar
            displayName={sessionProfile.displayName}
            userId={sessionProfile.userId}
            role={sessionProfile.role}
            onSignOut={handleSignOut}
          />
        </Col>

        {/* Right panel — tabs */}
        <Col xs={24} lg={17}>
          <Card style={{ borderRadius: 16, border: "1px solid #f0f0f0" }}>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={[
                {
                  key: PROFILE_TABS.PROFILE,
                  label: (
                    <Space>
                      <UserOutlined />
                      Profile Details
                    </Space>
                  ),
                  children: (
                    <ProfileDetailsTab
                      form={profileForm}
                      editMode={editMode}
                      saving={saveProfileMutation.isPending}
                      sessionProfile={sessionProfile}
                      securityQuestions={securityQuestions}
                      securityQuestionsLoading={securityQuestionsQuery.isLoading}
                      onEdit={handleEdit}
                      onCancel={handleCancelEdit}
                      onSave={handleSaveProfile}
                    />
                  ),
                },
                {
                  key: PROFILE_TABS.PASSWORD,
                  label: (
                    <Space>
                      <LockOutlined />
                      Change Password
                    </Space>
                  ),
                  children: (
                    <ChangePasswordTab
                      form={pwdForm}
                      changing={changePasswordMutation.isPending}
                      pwdStrength={pwdStrength}
                      securityQuestions={securityQuestions}
                      securityQuestionsLoading={securityQuestionsQuery.isLoading}
                      onPasswordChange={handlePasswordFieldChange}
                      onSubmit={handleChangePassword}
                    />
                  ),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

